import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getFieldValue, RichText } from "@sitecore-jss/sitecore-jss-react";
import {
  useUiValue,
  setNationalNotificationStatus,
  setNationalNotificationHeight,
} from "stores/uiContext";
import styled from "styled-components";
import cx from "classnames";
import { useMeasure } from "react-use";
import ChevronDownIcon from "components/icons/ChevronDown";
import { useCountryValue } from "stores/country";
import { useSpring, animated } from "react-spring";
import CloseIcon from "components/icons/Close";
import breakpoints from "utils/breakpoints";

const BannerContainer = styled.div`
  min-height: 3.73rem;
  padding-left: 1.375rem;
  padding-right: 0.875rem;
  @media ${breakpoints.md} {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
  }
`;

const HIDE_NATIONAL_BANNER = "hideNationalPromoBanner=";
const NationalPromoBanner = ({ fields = {} }) => {
  let hideNotificationSession = false;
  if (typeof window !== "undefined" && document) {
    hideNotificationSession =
      typeof document.cookie === "string" &&
      document.cookie.includes(HIDE_NATIONAL_BANNER + "true");
  }

  const [ref, { height }] = useMeasure();
  const [{ country }] = useCountryValue();
  const promotionBanner = getFieldValue(fields, "promotionBanner");
  const [promotionValues, setPromotionValues] = useState({});
  const [
    { nationalNotificationActive, hasCommingSoonCommunity },
    dispatch,
  ] = useUiValue();
  const [showCompleteBanner, setShowCompleteBanner] = useState(true);

  useEffect(() => {
    if (country === "USA") {
      setPromotionValues(promotionBanner.promotionBannerUSA);
    } else setPromotionValues(promotionBanner.promotionBannerCA);
  }, [country]);

  useEffect(() => {
    if (hasCommingSoonCommunity) dispatch(setNationalNotificationStatus(false));
    else
      dispatch(
        setNationalNotificationStatus(
          promotionValues.displayBanner && !hideNotificationSession
        )
      );
  }, [promotionValues, hasCommingSoonCommunity]);

  const textAlignment = "center"; // right  left  center
  const [fixedBarVisible, setFixedBarVisible] = useState(false);

  const handleOnScroll = () => {
    const currentScrollY = window.scrollY;
    const offset = 10;
    if (currentScrollY > offset) {
      setFixedBarVisible(true);
    } else if (fixedBarVisible) {
      setFixedBarVisible(false);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleOnScroll);
    return () => window.removeEventListener("scroll", handleOnScroll);
  }, [handleOnScroll]);

  useEffect(() => {
    if (
      nationalNotificationActive &&
      promotionValues &&
      promotionValues.line2
    ) {
      dispatch(setNationalNotificationHeight(height));
    } else {
      dispatch(setNationalNotificationHeight(0));
    }
  }, [height, nationalNotificationActive]);

  //check for links after rendinder Richtext Componenet
  useEffect(() => {
    const bannerContainer = document.getElementById(
      "national-notification-banner"
    );
    if (bannerContainer) {
      const links = bannerContainer.querySelectorAll("a");
      // If any links are found, make them focusable and assign a unique ID
      links.forEach((link, index) => {
        link.tabIndex = 0;
        //  link.id = `promo-link-${index}`; // Assign unique ID to each link based on index
      });
    }
  }, [promotionValues]);

  const fixedBarStyle = useSpring({
    height: height ? height : "auto",
    config: { duration: 100 },
    top: 0,
  });

  const closeNotification = () => {
    dispatch(setNationalNotificationStatus(false));
    const d = new Date();
    d.setTime(d.getTime() + 15 * 60 * 1000);
    document.cookie =
      HIDE_NATIONAL_BANNER + "true; expires=" + d.toUTCString() + ";";
  };

  if (
    !nationalNotificationActive ||
    !(promotionValues && promotionValues.line2)
  ) {
    return null;
  }

  return ReactDOM.createPortal(
    <animated.div
      className={cx("bg-aqua z-40 md:z-50", {
        "relative ": !fixedBarVisible, // || isMobile,
        "fixed top-0": fixedBarVisible, // && !isMobile,
      })}
      style={fixedBarStyle}
    >
      <BannerContainer
        ref={ref}
        className={cx("bg-aqua w-screen flex items-start justify-between ", {
          absolute: !!height,
        })}
        role={"region"}
        aria-label={"national notification"}
      >
        <div
          className={cx("w-full py-4 flex flex-col", {
            "justify-center": textAlignment === "center",
            "justify-start": textAlignment === "left",
            "justify-end": textAlignment === "right",
          })}
        >
          {showCompleteBanner && promotionValues.line1 && (
            <span
              className={cx("text-promo-dark-blue font-graphie mr-4 w-full", {
                "text-center": textAlignment === "center",
                "text-left": textAlignment === "left",
                "text-right": textAlignment === "right",
              })}
            >
              <RichText
                tag="span"
                className="richtext"
                field={{ value: promotionValues.line1 }}
              ></RichText>
            </span>
          )}
          <span
            className={cx(
              "text-promo-dark-blue font-trade-gothic-20 mr-4 w-full",
              {
                "text-center": textAlignment === "center",
                "text-left": textAlignment === "left",
                "text-right": textAlignment === "right",
              }
            )}
          >
            <RichText
              tag="span"
              className="richtext"
              field={{ value: promotionValues.line2 }}
            ></RichText>
          </span>
          {showCompleteBanner && promotionValues.line3 && (
            <span
              className={cx("text-promo-dark-blue font-graphie mr-4 w-full", {
                "text-center": textAlignment === "center",
                "text-left": textAlignment === "left",
                "text-right": textAlignment === "right",
              })}
            >
              <RichText
                tag="span"
                className="richtext"
                field={{ value: promotionValues.line3 }}
                onKeyDown={(event) => {
                  if (
                    event.key === "Tab" &&
                    !event.shiftKey &&
                    !promotionValues.ctaText
                  ) {
                    event.preventDefault();
                    const firstHeaderFocusableElement = document.getElementById(
                      "logo-index"
                    );
                    if (firstHeaderFocusableElement) {
                      firstHeaderFocusableElement.focus();
                    }
                  }
                }}
              ></RichText>
            </span>
          )}
          {promotionValues.ctaText && promotionValues.ctaLink && (
            <div
              className={cx("w-full  flex items-center justify-center ", {
                "text-center": textAlignment === "center",
                "text-left": textAlignment === "left",
                "text-right": textAlignment === "right",
              })}
            >
              <a
                tabIndex={0}
                key={promotionValues.ctaText}
                variant={"filled"}
                color={"mattamy-blue"}
                textColor={"white"}
                className={cx(
                  "rounded uppercase font-trade-gothic-20 py-4 px-8 tracking-wide w-auto mt-4 w-auto text-white bg-mattamy-blue",
                  {}
                )}
                href={promotionValues.ctaLink.href}
                linkWrapperDescription={promotionValues.ctaText}
                aria-label={promotionValues.ctaText}
                onClick={(e) => {
                  e.nativeEvent.stopPropagation();
                  if (typeof window !== "undefined") {
                    window.dataLayer.push({
                      event: "PromoBannerClick",
                    });
                    window.gtag("event", "PromoBannerClick");
                  }
                }}
              >
                <div className="mt-1 flex justify-center w-auto">
                  {promotionValues.ctaText}
                </div>
              </a>
            </div>
          )}
        </div>
        <div className="flex flex-col-reverse h-full w-8">
          <button
            id="national-notification-banner_index"
            className={cx("absolute bottom-0 pb-4 ", {
              hidden: !(promotionValues.line1 || promotionValues.line3),
            })}
            aria-label="Notification banner"
            aria-expanded={showCompleteBanner}
            onClick={() => {
              setShowCompleteBanner(!showCompleteBanner);
            }}
            onKeyDown={(event) => {
              if (
                event.key === "Tab" &&
                !event.shiftKey &&
                !promotionValues.line1 &&
                !promotionValues.line2 &&
                !promotionValues.line3 &&
                !promotionValues.ctaText &&
                !promotionValues.ctaLink
              ) {
                event.preventDefault();
                const firstHeaderFocusableElement = document.getElementById(
                  "logo-index"
                );
                if (firstHeaderFocusableElement) {
                  firstHeaderFocusableElement.focus();
                }
              }
            }}
            onBlur={(e) => {
              e.preventDefault();
              const containers = [
                document.getElementById("national-notification-banner"),
                document.getElementById("market-notification-banner"),
                document.getElementById("notification-banner"),
              ];
              if (containers) {
                containers.forEach((container) => {
                  if (container) {
                    const focusableElements = container.querySelectorAll(
                      "a, button"
                    );
                    focusableElements.forEach((element) => {
                      element.setAttribute("tabindex", "0");
                    });
                  }
                });
              }
            }}
          >
            <ChevronDownIcon
              className={cx("w-5 h-5 ml-1 mt-1 text-mattamy-blue transform ", {
                "rotate-180": showCompleteBanner,
                "rotate-270": !showCompleteBanner,
              })}
            />
          </button>
          <button
            className="mt-4 hidden"
            onClick={closeNotification}
            aria-label="Close national promo banner"
          >
            <CloseIcon className="text-mattamy-blue w-6 h-6" />
          </button>
        </div>
      </BannerContainer>
    </animated.div>,
    document.getElementById("national-notification-banner")
  );
};

export default NationalPromoBanner;
